import { useState } from 'react';
import { usePipelineHistory } from '../hooks/use-pipelines';
import { PipelineRunStatus } from '../types/pipeline';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

interface ExecutionHistoryPanelProps {
  pipelineId: string;
  onSelectRun?: (runId: string) => void;
}

export function ExecutionHistoryPanel({ pipelineId, onSelectRun }: ExecutionHistoryPanelProps) {
  const [page, setPage] = useState(0);
  const limit = 10;
  const offset = page * limit;

  const { data, isLoading, error, refetch } = usePipelineHistory(pipelineId);

  const getStatusColor = (status: PipelineRunStatus) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-500/10 text-green-500 hover:bg-green-500/20';
      case 'FAILED':
        return 'bg-red-500/10 text-red-500 hover:bg-red-500/20';
      case 'RUNNING':
        return 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20';
      case 'PENDING':
        return 'bg-gray-500/10 text-gray-500 hover:bg-gray-500/20';
      case 'CANCELLED':
        return 'bg-orange-500/10 text-orange-500 hover:bg-orange-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 hover:bg-gray-500/20';
    }
  };

  const formatDuration = (ms: number | null | undefined) => {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading execution history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
        <p className="text-sm text-destructive">
          Failed to load execution history. Please try again.
        </p>
      </div>
    );
  }

  if (!data || data.runs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center">
        <p className="text-sm text-muted-foreground">
          No execution history yet. Run the pipeline to see execution records.
        </p>
      </div>
    );
  }

  const totalPages = Math.ceil((data.totalRuns || 0) / limit);
  const hasNextPage = data.hasMore;
  const hasPreviousPage = page > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Execution History</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead className="text-right">Records Processed</TableHead>
              <TableHead className="text-right">Success</TableHead>
              <TableHead className="text-right">Failed</TableHead>
              <TableHead>Triggered By</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.runs.map((run) => (
              <TableRow
                key={run.runId}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onSelectRun?.(run.runId)}
              >
                <TableCell>
                  <Badge variant="outline" className={getStatusColor(run.status)}>
                    {run.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">
                  {formatDistanceToNow(new Date(run.startedAt), { addSuffix: true })}
                </TableCell>
                <TableCell className="text-sm">
                  {formatDuration(run.duration)}
                </TableCell>
                <TableCell className="text-right text-sm">
                  {run.recordsProcessed?.toLocaleString() || '-'}
                </TableCell>
                <TableCell className="text-right text-sm text-green-600">
                  {run.recordsSuccess?.toLocaleString() || '-'}
                </TableCell>
                <TableCell className="text-right text-sm text-red-600">
                  {run.recordsFailed?.toLocaleString() || '-'}
                </TableCell>
                <TableCell className="text-sm">
                  <Badge variant="secondary">{run.triggeredBy}</Badge>
                </TableCell>
                <TableCell>
                  {run.errorMessage && (
                    <Badge variant="destructive" className="text-xs">
                      Error
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {offset + 1} to {Math.min(offset + limit, data.totalRuns)} of{' '}
            {data.totalRuns} runs
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={!hasPreviousPage}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNextPage}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
